import React, {
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import Filters from 'core/Filters';
import {
  useFilterQueryWithDateRange,
  useTableReset,
} from 'core/hooks';
import SearchIcon from 'core/icons/search';
import {
  FilterableOptionDTO,
  FilterableSettingDTO,
  ToDoFilterDTO,
  ToDoPaginatedCollectionSummaryDTO,
} from 'dtos';
import {
  SortOrder,
} from 'dtos/sortOrder';
import {
  ToDoPillFilters,
} from 'dtos/toDoPillFilters';
import {
  ToDoSortKey,
} from 'dtos/toDoSortKey';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import ToolTip from 'core/components/Tooltip';
import Skeleton from 'core/components/Skeleton';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import {
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Overview from 'pages/Dashboard/pages/Todos/Overview';
import {
  FilterFieldClassNameRule,
  FilterInstance,
} from 'core/Filters/types';
import ToDoSortMenu from 'pages/Dashboard/pages/Todos/components/ToDoSortMenu';
import useSignalREffect from 'views/EMR/hooks/signalR';
import {
  ToDoSortValue,
} from 'pages/Dashboard/pages/Todos/types';
import {
  useToDoSettings,
} from 'pages/Dashboard/pages/Todos/hooks';
import {
  removeEmptyFilterValues,
} from 'core/Filters/util';
import useStorageState from 'core/hooks/storage';

const DEFAULT_SUMMARY_TAB = ToDoPillFilters.All;
const SUMMARY_TAB_STORAGE_KEY = 'toDosSummaryTab';
const defaultSortValue: ToDoSortValue = {
  sortBy: ToDoSortKey.Default,
  sortOrder: SortOrder.Descending,
};

const toDoFilterFieldClassNameRules: FilterFieldClassNameRule[] = [
  {
    name: 'Completed',
    type: 'checkbox',
    className: '!mt-2',
  },
];

type SummaryTab = keyof NonNullable<ToDoPaginatedCollectionSummaryDTO['counts']>;

type SummaryTabConfig = {
  tab: SummaryTab;
  label: string;
};

function normalizeSummaryTabKey(value?: string | null): string {
  return (value ?? '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
}

function humanizeSummaryTab(tab: string): string {
  return tab
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
}

function isAllSummaryTab(tab?: string | null): boolean {
  return normalizeSummaryTabKey(tab) === normalizeSummaryTabKey(ToDoPillFilters.All);
}

function isAssignedToMeSummaryTab(tab?: string | null): boolean {
  const normalizedValue = normalizeSummaryTabKey(tab);

  return normalizedValue === normalizeSummaryTabKey(ToDoPillFilters.AssignedToMe)
    || normalizedValue === 'assigneeidlist';
}

function toSummaryTabLabel(
  tab: SummaryTab,
  summaryTabConfigs?: SummaryTabConfig[] | null,
): string {
  const matchingTab = summaryTabConfigs?.find((config) => config.tab === tab);

  if (matchingTab?.label) {
    return matchingTab.label;
  }

  return humanizeSummaryTab(tab);
}

function toSummaryTabValue(
  value?: string | null,
  fallbackValue?: string | null,
): SummaryTab | null {
  if (typeof value === 'string' && notEmptyString(value)) {
    return value as SummaryTab;
  }

  if (typeof fallbackValue === 'string' && notEmptyString(fallbackValue)) {
    return fallbackValue as SummaryTab;
  }

  return null;
}

function toSummaryTabApiValue(tab: SummaryTab): ToDoPillFilters {
  if (isAssignedToMeSummaryTab(tab)) {
    return ToDoPillFilters.AssignedToMe;
  }

  return tab as ToDoPillFilters;
}

function getDefaultSummaryTabConfigs(): SummaryTabConfig[] {
  return Object.values(ToDoPillFilters).map((tab) => ({
    tab,
    label: humanizeSummaryTab(tab),
  }));
}

function getSummaryTabConfigs(
  filterablePillSettings?: FilterableSettingDTO[] | null,
): SummaryTabConfig[] {
  if (isNil(filterablePillSettings) || filterablePillSettings.length === 0) {
    return getDefaultSummaryTabConfigs();
  }

  const seenTabs = new Set<SummaryTab>();
  const summaryTabConfigs = filterablePillSettings.reduce<SummaryTabConfig[]>(
    (result, setting) => {
      const options = setting.optionList ?? [];
      const pillEntries = options.length > 0
        ? options
        : [{
          displayName: setting.displayName,
          value: setting.dtoName,
        } satisfies FilterableOptionDTO];

      pillEntries.forEach((option) => {
        const tab = toSummaryTabValue(option.value, setting.dtoName);

        if (isNil(tab) || seenTabs.has(tab)) {
          return;
        }

        seenTabs.add(tab);
        const label = typeof option.displayName === 'string' && notEmptyString(option.displayName)
          ? option.displayName
          : toSummaryTabLabel(tab);

        result.push({
          tab,
          label,
        });
      });

      return result;
    },
    [],
  );

  return summaryTabConfigs.length > 0
    ? summaryTabConfigs
    : getDefaultSummaryTabConfigs();
}

function getSummaryTabCountTextClass(tab: SummaryTab): string {
  switch (tab) {
    case ToDoPillFilters.OverDue:
      return 'text-red-500';
    case ToDoPillFilters.DueToday:
      return 'text-amber-500';
    case ToDoPillFilters.InProgress:
      return 'text-blue-500';
    default:
      return 'text-gray-900';
  }
}

type SummaryTabButtonProps = {
  isActive: boolean;
  isLoading: boolean;
  label: string;
  summaryTabCount?: number;
  tab: SummaryTab;
  onClick: (tab: SummaryTab) => void;
};

function SummaryTabButton({
  isActive,
  isLoading,
  label,
  summaryTabCount,
  tab,
  onClick,
}: SummaryTabButtonProps) {
  return (
    <button
      type="button"
      className={clsx(
        'relative flex min-h-[84px] min-w-0 w-full flex-col items-start justify-center gap-1 px-4 py-4 text-left transition-colors duration-150 sm:px-5',
        {
          'z-10 bg-[#f5f8ff] shadow-[inset_0_-3px_0_0_#2f64ff]': isActive,
          'bg-white hover:bg-[#fafcff]': !isActive,
        },
      )}
      onClick={() => { onClick(tab); }}
    >
      {isLoading ? (
        <Skeleton
          variant="rounded"
          className="!h-7 !w-10"
        />
      ) : !isNil(summaryTabCount) && (
        <span
          className={clsx(
            'text-[28px] font-semibold leading-none',
            getSummaryTabCountTextClass(tab),
          )}
        >
          {summaryTabCount}
        </span>
      )}
      <span
        className={clsx(
          'pointer-events-none w-full min-w-0 truncate whitespace-nowrap text-sm font-medium leading-5',
          {
            'text-blue-700': isActive,
            'text-gray-500': !isActive,
          },
        )}
      >
        <span>{label}</span>
      </span>
    </button>
  );
}

export default function ToDosView() {
  const {
    reset,
    tableInstanceRef,
  } = useTableReset();
  const { filterablePillSettings } = useToDoSettings();
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery();
  const [sortValue, setSortValue] = useState<ToDoSortValue>(defaultSortValue);
  const [summary, setSummary] = useState<ToDoPaginatedCollectionSummaryDTO | null>(null);
  const {
    filterQuery,
    setFilterQuery,
    filterInstanceRef,
    dateRange,
    setDateRange,
  } = useFilterQueryWithDateRange<ToDoFilterDTO>(
    'toDos',
    { uniqueKey: 'todosRange' },
    true,
  );
  const [activeSummaryTab, setActiveSummaryTab] = useStorageState<SummaryTab>(
    SUMMARY_TAB_STORAGE_KEY,
    DEFAULT_SUMMARY_TAB,
  );
  const summaryTabConfigs = useMemo(
    () => getSummaryTabConfigs(filterablePillSettings),
    [filterablePillSettings],
  );
  const defaultSummaryTab = useMemo(
    () => summaryTabConfigs.find(({ tab }) => isAllSummaryTab(tab))?.tab
      ?? summaryTabConfigs[0]?.tab
      ?? DEFAULT_SUMMARY_TAB,
    [summaryTabConfigs],
  );
  const activePillFilters = useMemo(
    () => [toSummaryTabApiValue(activeSummaryTab)],
    [activeSummaryTab],
  );

  useEffect(() => {
    const hasValidActiveTab = notEmptyString(activeSummaryTab)
      && summaryTabConfigs.some((config) => config.tab === activeSummaryTab);

    if (!hasValidActiveTab) {
      setActiveSummaryTab(defaultSummaryTab);
    }
  }, [activeSummaryTab, defaultSummaryTab, setActiveSummaryTab, summaryTabConfigs]);

  const onFilterUpdate = () => {
    const nextFilterQuery = filterInstanceRef?.current?.getFilterQuery?.() ?? {};

    if (!isEqual(
      removeEmptyFilterValues(nextFilterQuery),
      removeEmptyFilterValues(filterQuery),
    )) {
      setSummary(null);
      setFilterQuery(nextFilterQuery);
      reset();
    }
  };

  useEffect(() => {
    const unsubscribeFromSummary = toDosEventChannel.on('onToDoSummaryChanged', (nextSummary) => {
      setSummary(nextSummary);
    });
    const unsubscribeFromChanges = toDosEventChannel.on('onToDosChanged', () => {
      setSummary(null);
    });

    return () => {
      unsubscribeFromSummary();
      unsubscribeFromChanges();
    };
  }, []);

  useSignalREffect('OnToDoUpdated', (toDoId?: number) => {
    toDosEventChannel.emit('onToDosChanged', toDoId);
  }, []);

  const handleSummaryTabClick = (tab: SummaryTab) => {
    setActiveSummaryTab(tab);
    reset();
  };

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-col overflow-x-hidden">
      <div className="flex shrink-0 flex-col gap-4 px-4 pt-4">
          <div className="min-w-0">
            <h3 className="text-xl font-semibold leading-none text-gray-900">To-dos</h3>
          </div>
        <div className="flex w-full min-w-0 items-center gap-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <SearchInput
              placeholder="Search patient name, phone or date of birth"
              className="!w-96"
              value={searchQuery}
              onChange={(val) => {
                setSummary(null);
                setSearchQuery(val);
                reset();
              }}
              inputProps={{
                autoComplete: 'off',
              }}
              icon={<SearchIcon className="!h-4 !w-4 text-gray-400" />}
              id="patient-search"
            />
            <ToolTip title="Created on date range">
              <div>
                <DateRangePicker
                  value={dateRange}
                  onChange={(nextDateRange) => {
                    if (nextDateRange && !isEqual(nextDateRange, dateRange)) {
                      setSummary(null);
                      setDateRange(nextDateRange);
                      reset();
                    }
                  }}
                />
              </div>
            </ToolTip>
            <Filters
              pageObject="toDos"
              onFilterUpdate={onFilterUpdate}
              instanceRef={filterInstanceRef as RefObject<FilterInstance>}
              fieldClassNameRules={toDoFilterFieldClassNameRules}
              useStorage
            />
            <ToDoSortMenu
              value={sortValue}
              onChange={(nextSortValue) => {
                setSortValue(nextSortValue);
                reset();
              }}
            />
          </div>
          <Button
            className="nw-create-todo-button !h-8"
            variant="contained"
            type="button"
            color="primary"
            startIcon={<AddIcon className="!w-4 !h-4" />}
            onClick={() => {
              toDosEventChannel.emit('onCreateToDo', {});
            }}
          >
            Create To-do
          </Button>
        </div>
      </div>
      <div className="mt-4 min-w-0 overflow-x-auto overflow-y-hidden px-4">
        <div className="min-w-0 overflow-hidden rounded-md border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03),0_4px_10px_rgba(15,23,42,0.04)]">
          <div className="flex w-full min-w-max items-stretch divide-x divide-gray-200">
            {summaryTabConfigs.map(({ tab, label }) => (
              <SummaryTabButton
                key={tab}
                tab={tab}
                label={label}
                isActive={activeSummaryTab === tab}
                isLoading={isNil(summary)}
                summaryTabCount={summary?.counts?.[tab] ?? 0}
                onClick={handleSummaryTabClick}
              />
            ))}
          </div>
        </div>
      </div>
      <Overview
        classes="pt-3"
        dateRange={dateRange}
        filterQuery={filterQuery}
        pillFilters={activePillFilters}
        searchQuery={searchQuery}
        sortRequest={sortValue}
        tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
      />
    </div>
  );
}
